// THE RAIL — the client page's instrument: five scalar fields, always the same five,
// always in this order:
//
//     1 ENGAGEMENTS   2 DAY   3 BALL   4 LAST CONTACT   5 NEW
//
// Invariance is a SERVER property (briefQueries.readout always returns five entries in this
// order, a field it cannot compute being '—' with a tone of 'nil'), so this component has no
// branch that could reflow: it maps the array. Nothing here derives anything — value, unit,
// detail and tone are read straight off the payload, and the route is a constant per key.
//
// Every cell is client-scoped by construction. After v3.1 no rail field touches the gate
// engine, which is why a gate failure degrades the engagement cards and leaves the rail whole.
import { dressRefs } from './BriefText.js';
import { useT } from '../i18n/index.js';
import './ClientRail.de.js';
import './ClientRail.fr.js';
import { Rail, RailCell, type RailTone } from './PageKit.js';
import type { BriefField, RouteHandler } from '../api/brief.js';

const LABEL: Record<string, string> = {
  engagements: 'Engagements',
  day: 'Day',
  ball: 'Ball',
  last_contact: 'Last contact',
  new: 'New',
};

/** BALL says WITH US / WITH THEM — a bare pronoun in a 21px figure reads as a code. */
function figure(field: BriefField, t: ReturnType<typeof useT>): string {
  if (field.key === 'ball' && field.value !== '—') return t('WITH {who}', { who: field.value });
  return field.value;
}

export default function ClientRail({ readout, onRoute }: { readout: BriefField[]; onRoute: RouteHandler }) {
  const t = useT();
  return (
    <Rail>
      {readout.map((field) => (
        <RailCell
          key={field.key}
          label={t(LABEL[field.key] ?? field.key)}
          go={field.route ? '→' : undefined}
          value={figure(field, t)}
          unit={field.unit || undefined}
          tone={field.tone as RailTone}
          detail={<span className="voice-figure">{dressRefs(field.detail)}</span>}
          onClick={field.route ? () => { const r = field.route; if (r) onRoute(r); } : undefined}
        />
      ))}
    </Rail>
  );
}
